import { useMemo } from 'react';
import {
  useGetOneLinkFeatureConfigQuery,
  ONELINK_CONFIG_NAME,
  OneLinkConfig,
  UNDERSCORE_LOCALE_MAP,
} from '@dx-ui/framework-i18n';
import { isBrowser } from '@dx-ui/utilities-is-browser';
import { useIsClient } from 'usehooks-ts';
import { useGetLanguageSelectorHotelDataQuery } from '@dx-ui/queries-osc-language-selector';
import type { GetLanguageSelectorHotelDataQuery } from '@dx-ui/queries-osc-language-selector';
import { LANGUAGE_CODE_NAMES } from '../osc-language-code-names';
import { isRtl } from '@dx-ui/utilities-get-language-direction';
import { RuleEngine } from '@dx-shared/rule-engine';
import { logWarning } from '@dx-ui/framework-logger';

type LanguagesDict = { label: string; value: string; href: string; opensNewTab: boolean }[];

type SupportedLanguages = NonNullable<
  NonNullable<GetLanguageSelectorHotelDataQuery['hotel']>['localization']
>['languageSupport'];

type UrlOverride = {
  [locale in keyof typeof LANGUAGE_CODE_NAMES]?: string;
};

type UrlOverrides = {
  fullSite?: UrlOverride;
  resOnly?: UrlOverride;
};

export function useLanguageSelectorLanguages({
  appName,
  ctyhocn,
  customRuleEngine,
  removeUrlParams,
  urlOverrides,
}: {
  appName: string;
  ctyhocn: string;
  customRuleEngine?: RuleEngine | null;
  removeUrlParams?: boolean;
  urlOverrides?: UrlOverrides;
}) {
  const { data: featureConfigsData, isFetching: featureConfigsLoading } =
    useGetOneLinkFeatureConfigQuery();
  const { data: hotelData, isFetching: hotelDataLoading } = useGetLanguageSelectorHotelDataQuery(
    { ctyhocn: ctyhocn.toUpperCase() },
    { enabled: !!ctyhocn }
  );
  const isClient = useIsClient();
  const loading = !isClient || featureConfigsLoading || hotelDataLoading;
  const url = isBrowser ? new URL(window.location.href).pathname : '';
  const oneLinkLanguagesConfig: OneLinkConfig | null =
    featureConfigsData?.featureConfigs?.find((config) => config.name === ONELINK_CONFIG_NAME)
      ?.config || null;

  const oneLinkRuleEngine = oneLinkLanguagesConfig?.conditions
    ? new RuleEngine({
        name: 'onelink-language-selector',
        conditions: oneLinkLanguagesConfig.conditions,
      })
    : null;

  const supportedLanguages = (oneLinkLanguagesConfig?.supportedLanguages || []).filter(
    (language) => {
      const facts = { appName, language, url };
      return customRuleEngine?.run(facts) ?? oneLinkRuleEngine?.run(facts);
    }
  );

  const cmsLanguages = hotelData?.hotel?.localization?.languageSupport
    ? hotelData?.hotel?.localization?.languageSupport.reduce((acc, language) => {
        if (
          !supportedLanguages?.includes(getI18nSafeLangCode(language?.code || '')) &&
          getI18nSafeLangCode(language?.code || '') !== 'en'
        ) {
          acc.push({
            ...language,
            code: getI18nSafeLangCode(language?.code || ''),
          });
        }
        return acc;
      }, [] as SupportedLanguages)
    : ([] as SupportedLanguages);

  const cmsFullSiteLanguages = cmsLanguages.filter((lang) => lang?.siteUrl);
  const cmsResOnlyLanguages = cmsLanguages.filter((lang) => lang?.bookingUrl);
  const ENOverride = urlOverrides?.fullSite?.['en'];
  const fullSiteLanguages: LanguagesDict = [
    {
      label: LANGUAGE_CODE_NAMES['en'],
      value: 'en',
      href: (ENOverride || makeLanguageUrl('en', removeUrlParams)) ?? '',
      opensNewTab: !!ENOverride,
    },
    ...supportedLanguages.map((lang) => {
      const override = urlOverrides?.fullSite?.[lang as keyof typeof LANGUAGE_CODE_NAMES];
      const hasOverride = !!override;
      return {
        label: LANGUAGE_CODE_NAMES[lang as keyof typeof LANGUAGE_CODE_NAMES],
        value: lang,
        href: (override || makeLanguageUrl(lang, removeUrlParams)) ?? '',
        opensNewTab: hasOverride,
      };
    }),
    ...cmsFullSiteLanguages.map((lang) => ({
      label: LANGUAGE_CODE_NAMES[lang?.code as keyof typeof LANGUAGE_CODE_NAMES] || lang.label,
      value: lang?.code || '',
      href:
        urlOverrides?.fullSite?.[lang?.code as keyof typeof LANGUAGE_CODE_NAMES] ||
        lang?.siteUrl ||
        '',
      opensNewTab: true,
    })),
  ];
  const resOnlyLanguages: LanguagesDict = [
    ...cmsResOnlyLanguages.map((lang) => ({
      label: LANGUAGE_CODE_NAMES[lang?.code as keyof typeof LANGUAGE_CODE_NAMES] || lang.label,
      value: lang?.code || '',
      href:
        urlOverrides?.resOnly?.[lang.code as keyof typeof LANGUAGE_CODE_NAMES] ||
        lang?.bookingUrl ||
        '',
      opensNewTab: true,
    })),
  ];

  const supportedFullSiteLanguages = fullSiteLanguages.map((lang) => lang.value);
  const netNewFullSiteLanguages = Object.keys(urlOverrides?.fullSite || {}).filter(
    (lang) => !supportedFullSiteLanguages.includes(lang)
  );

  if (netNewFullSiteLanguages.length) {
    fullSiteLanguages.push(
      ...netNewFullSiteLanguages.map((lang) => ({
        label: LANGUAGE_CODE_NAMES[lang as keyof typeof LANGUAGE_CODE_NAMES],
        value: lang,
        href: urlOverrides?.fullSite?.[lang as keyof typeof LANGUAGE_CODE_NAMES] || '',
        opensNewTab: true,
      }))
    );
  }

  const supportedResOnlyLanguages = resOnlyLanguages.map((lang) => lang.value);
  const netNewResOnlyLanguages = Object.keys(urlOverrides?.resOnly || {}).filter(
    (lang) =>
      !supportedResOnlyLanguages.includes(lang) && !supportedFullSiteLanguages.includes(lang)
  );

  if (netNewResOnlyLanguages.length) {
    resOnlyLanguages.push(
      ...netNewResOnlyLanguages.map((lang) => ({
        label: LANGUAGE_CODE_NAMES[lang as keyof typeof LANGUAGE_CODE_NAMES],
        value: lang,
        href: urlOverrides?.resOnly?.[lang as keyof typeof LANGUAGE_CODE_NAMES] || '',
        opensNewTab: true,
      }))
    );
  }

  const sortedFullSiteLanguages = sortRtlFirst(
    filterLanguagesWithLabel(fullSiteLanguages).sort((a, b) => a.label.localeCompare(b.label))
  );
  const sortedResOnlyLanguages = sortRtlFirst(
    filterLanguagesWithLabel(resOnlyLanguages).sort((a, b) => a.label.localeCompare(b.label))
  );
  const allLanguages = useMemo(
    () => [...sortedFullSiteLanguages, ...sortedResOnlyLanguages],
    [sortedFullSiteLanguages, sortedResOnlyLanguages]
  );

  const POPULAR_LANGUAGES_ORDER = ['en', 'es', 'de', 'fr', 'ja', 'zh-hans'];

  const popularLanguagesOnly = POPULAR_LANGUAGES_ORDER.map((code) =>
    sortedFullSiteLanguages.find((lang) => lang.value === code)
  ).filter(Boolean) as typeof sortedFullSiteLanguages;

  const nonPopularLanguages = sortedFullSiteLanguages.filter(
    (lang) => !POPULAR_LANGUAGES_ORDER.includes(lang.value)
  );

  return {
    allLanguages,
    loading,
    sortedFullSiteLanguages,
    sortedResOnlyLanguages,
    popularLanguagesOnly,
    nonPopularLanguages,
  };
}

export function filterLanguagesWithLabel(languages: LanguagesDict) {
  return languages.filter((lang) => {
    if (!lang.label) {
      logWarning(
        'useLanguageSelectorLanguages',
        `Language selector label missing for ${lang.value}`
      );
    }
    return lang.label;
  });
}

export function getI18nSafeLangCode(code: string) {
  const lowerCasedLanguage = code.toLowerCase();
  if (UNDERSCORE_LOCALE_MAP[lowerCasedLanguage]) {
    return UNDERSCORE_LOCALE_MAP[lowerCasedLanguage];
  }

  const baseLanguageCode = code.split('_')[0];
  return baseLanguageCode?.toLowerCase() ?? 'en';
}

export function makeLanguageUrl(newLocale: string, removeUrlParams?: boolean) {
  const languageUrl = isBrowser
    ? window.location.href.replace(
        RegExp(`/${window.location.pathname.split('/')[1]}/`),
        `/${newLocale}/`
      )
    : '';
  return removeUrlParams ? languageUrl.split('?')[0] : languageUrl;
}

export function sortRtlFirst(languages: LanguagesDict) {
  const rtlLangs = languages.filter((lang) => isRtl(lang.value));
  const ltrLangs = languages.filter((lang) => !isRtl(lang.value));
  return [...rtlLangs, ...ltrLangs];
}
